import { sendPatientAction } from './patientWritesClient';
import { readVersionFromEtag, sendOutsideSaveAttempt } from './saveHttp';

/**
 * Hand-written client for the connector-next case endpoints. Every write is
 * guarded by the patient's version (If-Match) — a case is part of the patient
 * — which the generated client cannot send. No write is part of a dataset
 * save, so none carries a save attempt id. Each resolves to the patient's
 * next version.
 */

/** Makes a case of the given images and structure sets; resolves to its id and the patient's next version. */
export async function createCase(
    patientId: string,
    name: string,
    datasetIds: readonly string[],
    ifMatch: string
): Promise<{ id: string; version: string }> {
    const response = await sendOutsideSaveAttempt(
        `/api/patients/${patientId}/cases`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name, datasetIds }),
        },
        ifMatch
    );
    const body = (await response.json()) as { id?: unknown };
    if (typeof body.id !== 'string') {
        throw new Error('The Connector did not return the new case id.');
    }
    return { id: body.id, version: readVersionFromEtag(response) };
}

/** Renames a case and/or archives or restores it; an absent field is left alone. */
export function updateCase(
    patientId: string,
    caseId: string,
    change: { name?: string; archived?: boolean },
    ifMatch: string
): Promise<string> {
    return sendPatientAction(
        'PATCH',
        patientId,
        `cases/${caseId}`,
        ifMatch,
        change
    );
}

/** Adds datasets to a case, then removes datasets from it, in one change. */
export function changeCaseMembers(
    patientId: string,
    caseId: string,
    change: { add?: readonly string[]; remove?: readonly string[] },
    ifMatch: string
): Promise<string> {
    return sendPatientAction(
        'POST',
        patientId,
        `cases/${caseId}/members`,
        ifMatch,
        change
    );
}

/** Deletes a case. Its datasets stay on the patient. */
export function deleteCase(
    patientId: string,
    caseId: string,
    ifMatch: string
): Promise<string> {
    return sendPatientAction('DELETE', patientId, `cases/${caseId}`, ifMatch);
}

/** Names the case work opens on, or stops naming one with null. */
export function setWorkCase(
    patientId: string,
    caseId: string | null,
    ifMatch: string
): Promise<string> {
    return sendPatientAction('POST', patientId, 'work-case', ifMatch, {
        caseId,
    });
}
